<script setup>
import Select from 'dashboard/components-next/select/Select.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import ReorderableMultiSelect from 'dashboard/components-next/combobox/ReorderableMultiSelect.vue';
import CompactSingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import CompactMultiSelect from 'dashboard/components-next/filter/inputs/MultiSelect.vue';

defineProps({
  mode: {
    type: String,
    default: 'single',
    validator: value =>
      ['single', 'multiple', 'tags', 'reorderable'].includes(value),
  },
  presentation: {
    type: String,
    default: 'field',
    validator: value => ['field', 'compact'].includes(value),
  },
  options: { type: Array, default: () => [] },
  groups: { type: Array, default: () => [] },
  searchable: { type: Boolean, default: false },
  remoteSearch: { type: Boolean, default: false },
  placeholder: { type: String, default: '' },
  searchPlaceholder: { type: String, default: '' },
  emptyState: { type: String, default: '' },
  ariaLabel: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  error: { type: String, default: '' },
  message: { type: String, default: '' },
  messageClass: { type: String, default: '' },
  displayLabel: { type: String, default: '' },
  teleport: { type: Boolean, default: true },
  maxSelected: { type: Number, default: 3 },
  label: { type: String, default: '' },
  addLabel: { type: String, default: '' },
  fallbackIcon: { type: String, default: 'i-lucide-file-text' },
  asyncSearch: { type: Boolean, default: false },
  isSearching: { type: Boolean, default: false },
  disableSearch: { type: Boolean, default: false },
  disableDeselect: { type: Boolean, default: false },
  placeholderIcon: { type: String, default: 'i-lucide-plus' },
  placeholderTrailingIcon: { type: Boolean, default: false },
  dropdownMaxHeight: { type: String, default: 'max-h-80' },
  maxChips: { type: Number, default: 3 },
});

const emit = defineEmits(['search', 'open']);

const modelValue = defineModel({
  type: [String, Number, Boolean, Array, Object, null],
  default: null,
});
</script>

<template>
  <CompactMultiSelect
    v-if="
      presentation === 'compact' && (mode === 'multiple' || mode === 'tags')
    "
    v-model="modelValue"
    :options="options"
    :max-chips="maxChips"
    :dropdown-max-height="dropdownMaxHeight"
  />
  <CompactSingleSelect
    v-else-if="presentation === 'compact'"
    v-model="modelValue"
    :options="options"
    :async-search="asyncSearch || remoteSearch"
    :is-searching="isSearching"
    :disable-search="disableSearch"
    :disable-deselect="disableDeselect"
    :placeholder-icon="placeholderIcon"
    :placeholder="placeholder"
    :placeholder-trailing-icon="placeholderTrailingIcon"
    :search-placeholder="searchPlaceholder"
    :dropdown-max-height="dropdownMaxHeight"
    @search="emit('search', $event)"
  />
  <Select
    v-else-if="mode === 'single' && !searchable"
    v-model="modelValue"
    :options="options"
    :groups="groups"
    :placeholder="placeholder"
    :disabled="disabled"
    :error="error"
    :message="message"
    :message-class="messageClass"
    :search-placeholder="searchPlaceholder"
    :empty-state="emptyState"
    :aria-label="ariaLabel"
    :teleport="teleport"
  />
  <ComboBox
    v-else-if="mode === 'single'"
    v-model="modelValue"
    :options="options"
    :placeholder="placeholder"
    :display-label="displayLabel"
    :disabled="disabled"
    :search-placeholder="searchPlaceholder"
    :empty-state="emptyState"
    :message="error || message"
    :message-class="messageClass"
    :has-error="Boolean(error)"
    :use-api-results="remoteSearch"
    @search="emit('search', $event)"
    @open="emit('open')"
  />
  <TagMultiSelectComboBox
    v-else-if="mode === 'multiple' || mode === 'tags'"
    v-model="modelValue"
    :options="options"
    :placeholder="placeholder"
    :disabled="disabled"
    :search-placeholder="searchPlaceholder"
    :empty-state="emptyState"
    :message="error || message"
    :message-class="messageClass"
    :has-error="Boolean(error)"
  />
  <ReorderableMultiSelect
    v-else
    v-model="modelValue"
    :options="options"
    :max="maxSelected"
    :label="label"
    :add-label="addLabel"
    :search-placeholder="searchPlaceholder"
    :empty-state="emptyState"
    :fallback-icon="fallbackIcon"
    :loading="loading"
    :disabled="disabled"
    :server-search="remoteSearch"
    @search="emit('search', $event)"
  >
    <template v-if="$slots.counter" #counter="slotProps">
      <slot name="counter" v-bind="slotProps" />
    </template>
  </ReorderableMultiSelect>
</template>
